import { useState } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { genUserName } from '@/lib/genUserName';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { formatRelativeTime } from '@/lib/formatRelativeTime';
import { DITTO_RELAY } from '@/lib/ditto';
import { chunkAuthors } from '@/hooks/useInfiniteTimeline';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PostActions } from '@/desktop/components/PostActions';
import { NoteContent } from '@/components/NoteContent';
import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import { useDesktop } from '@/desktop/DesktopContext';
import { useNostrNavigate } from '@/hooks/useNostrNavigate';
import { BookOpen, RefreshCw, Users, Globe, ArrowLeft, Clock, ExternalLink, MessageCircle, Send } from 'lucide-react';

type ArticleTab = 'following' | 'global';

function formatDate(unixTs: number): string {
  return new Date(unixTs * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function estimateReadTime(content: string): number {
  const words = content.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
}

function getArticleMeta(event: NostrEvent) {
  const title = event.tags.find(([t]) => t === 'title')?.[1] ?? 'Untitled';
  const summary = event.tags.find(([t]) => t === 'summary')?.[1] ?? '';
  const image = event.tags.find(([t]) => t === 'image')?.[1] ?? '';
  const slug = event.tags.find(([t]) => t === 'd')?.[1] ?? '';
  const publishedAt = event.tags.find(([t]) => t === 'published_at')?.[1];
  const topics = event.tags.filter(([t]) => t === 't').map(([, v]) => v);
  return { title, summary, image, slug, publishedAt, topics };
}

// ─── Markdown rendering ───────────────────────────────────────────────────────

/**
 * Anchor that keeps article links inside nostrVM: https links open as a new tab
 * in the nsite browser, NIP-19 / njump links route to the right desktop window.
 * Falls back to a plain new-tab link outside the desktop.
 */
function MarkdownLink({ href, children, ...props }: { href?: string; children?: React.ReactNode }) {
  const { openUrl, openNip19 } = useNostrNavigate();
  const safe = sanitizeUrl(href);
  if (!safe) return <span {...props}>{children}</span>;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // njump.me/<nip19> and nostr:<nip19> links route to the matching window;
    // anything else opens as a new tab in the nsite browser.
    const njump = safe.match(/^https?:\/\/njump\.me\/((?:npub|nprofile|note|nevent|naddr)1[a-z0-9]+)/i);
    const bech32 = njump?.[1] ?? href?.replace(/^nostr:/, '');
    if (bech32 && /^(npub|nprofile|note|nevent|naddr)1[a-z0-9]+$/i.test(bech32) && openNip19(bech32, e)) {
      return;
    }
    openUrl(safe, e);
  };

  return (
    <a href={safe} onClick={handleClick} className="text-primary underline hover:no-underline cursor-pointer" {...props}>
      {children}
    </a>
  );
}

// react-markdown builds React elements (no innerHTML); `skipHtml` drops any raw
// HTML, and every author-supplied URL is run through sanitizeUrl (https-only).
const markdownComponents: Components = {
  h1: ({ node: _node, ...props }) => <h1 className="text-xl font-bold mt-6 mb-3 leading-tight" {...props} />,
  h2: ({ node: _node, ...props }) => <h2 className="text-lg font-bold mt-5 mb-2 leading-tight" {...props} />,
  h3: ({ node: _node, ...props }) => <h3 className="text-base font-semibold mt-4 mb-2" {...props} />,
  h4: ({ node: _node, ...props }) => <h4 className="text-sm font-semibold mt-4 mb-2" {...props} />,
  p: ({ node: _node, ...props }) => <p className="my-3 text-sm leading-relaxed" {...props} />,
  ul: ({ node: _node, ...props }) => <ul className="list-disc pl-5 my-3 space-y-1 text-sm" {...props} />,
  ol: ({ node: _node, ...props }) => <ol className="list-decimal pl-5 my-3 space-y-1 text-sm" {...props} />,
  li: ({ node: _node, ...props }) => <li className="leading-relaxed" {...props} />,
  blockquote: ({ node: _node, ...props }) => (
    <blockquote className="border-l-2 border-border pl-3 my-3 italic text-muted-foreground" {...props} />
  ),
  pre: ({ node: _node, ...props }) => (
    <pre className="bg-muted rounded-md p-3 overflow-x-auto text-xs my-3" {...props} />
  ),
  code: ({ node: _node, className, ...props }) => {
    const isBlock = !!className && className.includes('language-');
    return (
      <code
        className={isBlock ? 'font-mono text-xs' : 'bg-muted px-1 py-0.5 rounded text-xs font-mono'}
        {...props}
      />
    );
  },
  hr: ({ node: _node, ...props }) => <hr className="my-6 border-border/60" {...props} />,
  table: ({ node: _node, ...props }) => (
    <div className="overflow-x-auto my-3">
      <table className="w-full text-xs border-collapse" {...props} />
    </div>
  ),
  th: ({ node: _node, ...props }) => <th className="border border-border px-2 py-1 text-left font-semibold" {...props} />,
  td: ({ node: _node, ...props }) => <td className="border border-border px-2 py-1" {...props} />,
  a: ({ node: _node, href, children, ...props }) => (
    <MarkdownLink href={href} {...props}>{children}</MarkdownLink>
  ),
  img: ({ node: _node, src, alt }) => {
    const safe = sanitizeUrl(typeof src === 'string' ? src : undefined);
    if (!safe) return null;
    return <img src={safe} alt={alt ?? ''} loading="lazy" className="rounded-lg my-4 max-w-full h-auto" />;
  },
};

function ArticleMarkdown({ content }: { content: string }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml components={markdownComponents}>
      {content}
    </ReactMarkdown>
  );
}

// ─── Comments (NIP-22 / kind 1111) ────────────────────────────────────────────

function CommentRow({ event }: { event: NostrEvent }) {
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(event.pubkey);
  const { openProfile } = useDesktop();

  return (
    <div className="flex gap-2.5 py-3 border-b border-border/40 last:border-0">
      <button onClick={() => openProfile(event.pubkey)} className="shrink-0 mt-0.5">
        <Avatar className="w-7 h-7">
          <AvatarImage src={metadata?.picture} alt={displayName} />
          <AvatarFallback className="text-[9px]">{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-1.5">
          <button onClick={() => openProfile(event.pubkey)} className="text-xs font-medium hover:underline truncate max-w-[160px]">
            {displayName}
          </button>
          <span className="text-[10px] text-muted-foreground">{formatRelativeTime(event.created_at)}</span>
        </div>
        <NoteContent event={event} className="text-sm leading-relaxed mt-0.5" />
      </div>
    </div>
  );
}

function ArticleComments({ article }: { article: NostrEvent }) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { mutateAsync: publish, isPending } = useNostrPublish();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [text, setText] = useState('');

  const { slug } = getArticleMeta(article);
  const coord = `30023:${article.pubkey}:${slug}`;
  const queryKey = ['article-comments', coord];

  const { data: comments, isLoading } = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const events = await nostr.query([{ kinds: [1111], '#A': [coord], limit: 200 }], {
        signal: AbortSignal.any([signal, AbortSignal.timeout(15000)]),
      });
      return events.sort((a, b) => a.created_at - b.created_at);
    },
    staleTime: 30000,
  });

  const handleSubmit = async () => {
    const content = text.trim();
    if (!content) return;
    try {
      const published = await publish({
        kind: 1111,
        content,
        tags: [
          // Root scope (uppercase): the article.
          ['A', coord],
          ['K', '30023'],
          ['P', article.pubkey],
          // Parent item (lowercase): same as root for a top-level comment.
          ['a', coord],
          ['e', article.id],
          ['k', '30023'],
          ['p', article.pubkey],
        ],
      });
      setText('');
      queryClient.setQueryData<NostrEvent[]>(queryKey, (prev = []) =>
        [...prev, published].sort((a, b) => a.created_at - b.created_at)
      );
    } catch {
      toast({ title: 'Failed to post comment', description: 'Please try again.', variant: 'destructive' });
    }
  };

  return (
    <section className="mt-8 pt-4 border-t border-border/40">
      <div className="flex items-center gap-1.5 mb-3">
        <MessageCircle className="w-4 h-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold">
          Comments{comments && comments.length > 0 ? ` (${comments.length})` : ''}
        </h2>
      </div>

      {user ? (
        <div className="mb-4">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a comment…"
            className="text-sm min-h-[72px] resize-none"
          />
          <div className="flex justify-end mt-2">
            <Button size="sm" className="gap-1.5" onClick={handleSubmit} disabled={isPending || !text.trim()}>
              <Send className="w-3.5 h-3.5" />
              {isPending ? 'Posting…' : 'Comment'}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground mb-4">Log in to join the conversation.</p>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-2.5">
              <Skeleton className="w-7 h-7 rounded-full shrink-0" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-3 w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : comments && comments.length > 0 ? (
        <div>
          {comments.map((c) => (
            <CommentRow key={c.id} event={c} />
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No comments yet. Be the first to comment.</p>
      )}
    </section>
  );
}

// ─── Article reader (full view) ───────────────────────────────────────────────

export function ArticleReader({ event, onBack }: { event: NostrEvent; onBack: () => void }) {
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(event.pubkey);
  const { openProfile } = useDesktop();
  const { title, summary, image, slug, publishedAt, topics } = getArticleMeta(event);
  const readTime = estimateReadTime(event.content);
  const dateTs = publishedAt ? parseInt(publishedAt) : event.created_at;
  const safeImage = sanitizeUrl(image);

  const naddr = (() => {
    try {
      return nip19.naddrEncode({ kind: 30023, pubkey: event.pubkey, identifier: slug });
    } catch {
      return null;
    }
  })();

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/50 bg-muted/30 shrink-0">
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onBack}>
          <ArrowLeft className="w-3.5 h-3.5" />
        </Button>
        <span className="text-sm font-medium truncate flex-1">{title}</span>
        {naddr && (
          <a href={`/${naddr}`} target="_blank" rel="noopener noreferrer" className="shrink-0">
            <Button size="icon" variant="ghost" className="h-7 w-7">
              <ExternalLink className="w-3.5 h-3.5" />
            </Button>
          </a>
        )}
      </div>
      <ScrollArea className="flex-1 h-full">
        <div className="p-4 max-w-prose mx-auto pb-16">
          {safeImage && (
            <div className="rounded-lg overflow-hidden mb-4 -mx-4 sm:mx-0">
              <img src={safeImage} alt={title} className="w-full h-40 sm:h-52 object-cover" />
            </div>
          )}
          <h1 className="text-xl font-bold mb-3 leading-tight">{title}</h1>

          {/* Author + meta row */}
          <div className="flex items-center gap-2 mb-3 pb-3 border-b border-border/40">
            <button onClick={() => openProfile(event.pubkey)}>
              <Avatar className="w-7 h-7">
                <AvatarImage src={metadata?.picture} alt={displayName} />
                <AvatarFallback className="text-[9px]">{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
            </button>
            <div className="flex-1 min-w-0">
              <button className="text-xs font-medium hover:underline block truncate" onClick={() => openProfile(event.pubkey)}>{displayName}</button>
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                <span>{formatDate(dateTs)}</span>
                <span>·</span>
                <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{readTime}m read</span>
              </div>
            </div>
          </div>

          {/* Action bar on article itself */}
          <div className="mb-4 pb-3 border-b border-border/40">
            <PostActions event={event} />
          </div>

          {summary && (
            <p className="text-sm text-muted-foreground italic mb-4 leading-relaxed">{summary}</p>
          )}

          <div className="article-body">
            <ArticleMarkdown content={event.content} />
          </div>

          {topics.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-6 pt-4 border-t border-border/40">
              {topics.map((t) => (
                <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>
              ))}
            </div>
          )}

          <ArticleComments article={event} />
        </div>
      </ScrollArea>
    </div>
  );
}

// ─── Article list card (compact) ──────────────────────────────────────────────

function ArticleCard({ event, onOpen }: { event: NostrEvent; onOpen: (event: NostrEvent) => void }) {
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(event.pubkey);
  const { title, summary, image, publishedAt } = getArticleMeta(event);
  const readTime = estimateReadTime(event.content);
  const dateTs = publishedAt ? parseInt(publishedAt) : event.created_at;
  const safeImage = sanitizeUrl(image);

  return (
    <button
      onClick={() => onOpen(event)}
      className="w-full text-left p-3 border-b border-border/40 hover:bg-muted/20 transition-colors"
    >
      <div className="flex gap-3">
        {safeImage && (
          <img src={safeImage} alt={title} className="w-16 h-16 rounded-lg object-cover shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <Avatar className="w-5 h-5">
              <AvatarImage src={metadata?.picture} alt={displayName} />
              <AvatarFallback className="text-[8px]">{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <span className="text-[10px] text-muted-foreground truncate">{displayName}</span>
          </div>
          <h3 className="font-semibold text-sm leading-snug line-clamp-2 mb-0.5">{title}</h3>
          {summary && (
            <p className="text-xs text-muted-foreground line-clamp-2 mb-1">{summary}</p>
          )}
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
            <span>{formatDate(dateTs)}</span>
            <span>·</span>
            <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{readTime}m read</span>
          </div>
        </div>
      </div>
    </button>
  );
}

function ArticleListSkeleton() {
  return (
    <div>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="p-3 border-b border-border/40">
          <div className="flex gap-3">
            <Skeleton className="w-16 h-16 rounded-lg shrink-0" />
            <div className="flex-1 space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Skeleton className="w-5 h-5 rounded-full" />
                <Skeleton className="h-3 w-20" />
              </div>
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Following articles — a single self-contained query that loads contacts
 * inside its queryFn so the query key never oscillates.
 *
 * Articles are fetched as one REQ per ≤100-author chunk in parallel (see
 * FeedApp for why large authors filters come back empty from relays).
 */
function FollowingArticles({ onOpen }: { onOpen: (e: NostrEvent) => void }) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['articles', 'following', user?.pubkey],
    queryFn: async ({ signal }) => {
      if (!user) return { articles: [] as NostrEvent[], followCount: 0 };

      // 1. Fetch contacts.
      const contactEvents = await nostr.query(
        [{ kinds: [3], authors: [user.pubkey], limit: 1 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]) },
      );
      const followedPubkeys = contactEvents.length > 0
        ? contactEvents[0].tags.filter(([t]) => t === 'p').map(([, pk]) => pk).filter(Boolean)
        : [];

      const authorPubkeys = Array.from(new Set([user.pubkey, ...followedPubkeys]));
      if (authorPubkeys.length === 0) return { articles: [] as NostrEvent[], followCount: 0 };

      // 2. Chunk authors (≤100 per REQ) and query every chunk in parallel,
      // each as its own subscription. Failing chunks degrade to empty.
      const chunks = chunkAuthors(authorPubkeys);
      const perChunkLimit = chunks.length > 1
        ? Math.max(10, Math.ceil(50 / chunks.length))
        : 50;

      const results = await Promise.all(
        chunks.map((c) =>
          nostr.query([{ kinds: [30023], authors: c, limit: perChunkLimit }], {
            signal: AbortSignal.any([signal, AbortSignal.timeout(15000)]),
          }).catch(() => [] as NostrEvent[]),
        ),
      );

      // 3. Deduplicate & filter.
      const seen = new Set<string>();
      const merged: NostrEvent[] = [];
      for (const events of results) {
        for (const event of events) {
          if (!seen.has(event.id)) { seen.add(event.id); merged.push(event); }
        }
      }

      const articles = merged
        .filter((e) => e.tags.some(([t]) => t === 'title'))
        .sort((a, b) => b.created_at - a.created_at);

      return { articles, followCount: followedPubkeys.length };
    },
    enabled: !!user,
    staleTime: 60000,
  });

  const events = data?.articles ?? [];
  const followCount = data?.followCount ?? 0;

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground px-6 text-center">
        <BookOpen className="w-10 h-10 opacity-30" />
        <p className="text-sm">Log in to read articles from people you follow.</p>
      </div>
    );
  }

  if (isLoading) {
    return <ArticleListSkeleton />;
  }

  if (followCount === 0 && events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground px-6 text-center">
        <Users className="w-10 h-10 opacity-30" />
        <p className="text-sm">You're not following anyone yet. Switch to Global to discover articles.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/40 bg-muted/10">
        <span className="text-[10px] text-muted-foreground">{followCount} following</span>
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`w-3 h-3 ${isFetching ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      <div className="flex-1 overflow-auto min-h-0">
        {events.length > 0 ? (
          events.map((e) => <ArticleCard key={e.id} event={e} onOpen={onOpen} />)
        ) : (
          <div className="p-8 text-center text-muted-foreground text-sm">
            No articles from your follows yet.
          </div>
        )}
      </div>
    </div>
  );
}

function GlobalArticles({ onOpen }: { onOpen: (e: NostrEvent) => void }) {
  const { nostr } = useNostr();

  const { data: events, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['articles', 'global'],
    queryFn: async ({ signal }) => {
      const events = await nostr.query(
        [{ kinds: [30023], limit: 50 }],
        { relays: [DITTO_RELAY], signal: AbortSignal.any([signal, AbortSignal.timeout(15000)]) }
      );
      return events
        .filter((e) => e.tags.some(([t]) => t === 'title'))
        .sort((a, b) => b.created_at - a.created_at);
    },
    staleTime: 60000,
  });

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/40 bg-muted/10">
        <span className="text-[10px] text-muted-foreground">Latest articles</span>
        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`w-3 h-3 ${isFetching ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      <div className="flex-1 overflow-auto min-h-0">
        {isLoading ? (
          <ArticleListSkeleton />
        ) : events && events.length > 0 ? (
          events.map((e) => <ArticleCard key={e.id} event={e} onOpen={onOpen} />)
        ) : (
          <div className="p-8 text-center text-muted-foreground text-sm">
            No articles found. Check relay connections.
          </div>
        )}
      </div>
    </div>
  );
}

export function ArticlesApp() {
  const [tab, setTab] = useState<ArticleTab>('following');
  const [openArticle, setOpenArticle] = useState<NostrEvent | null>(null);

  if (openArticle) {
    return <ArticleReader event={openArticle} onBack={() => setOpenArticle(null)} />;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Tab bar */}
      <div className="flex items-center gap-0 px-3 py-2 border-b border-border/50 bg-muted/30 shrink-0">
        <button
          onClick={() => setTab('following')}
          className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-colors ${
            tab === 'following'
              ? 'bg-background shadow-sm text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Users className="w-3 h-3" />
          Following
        </button>
        <button
          onClick={() => setTab('global')}
          className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-colors ${
            tab === 'global'
              ? 'bg-background shadow-sm text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Globe className="w-3 h-3" />
          Global
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        {tab === 'following'
          ? <FollowingArticles onOpen={setOpenArticle} />
          : <GlobalArticles onOpen={setOpenArticle} />
        }
      </div>
    </div>
  );
}
